/**
 * /dashboard/settings/billing — Settings → Organization → Billing
 * (Phase 8.4).
 *
 *  - Shows the caller's own tenant subscription
 *    (GET /api/v1/billing/subscription): plan, status badge, started /
 *    renews-or-expires dates. A trial tenant resolves to
 *    { status: "trial", plan: null }.
 *  - For a company_admin: an upgrade / change-plan section that reuses
 *    the marketing pricing cards (<PlanPicker> → <PricingCards
 *    mode="billing">) and the SAME checkout flow (useCheckout). The
 *    current plan is shown as "Current plan" (disabled).
 *  - For a non-admin HR (hr_manager — Settings/Billing is HR-admin
 *    gated in nav): READ-ONLY. The picker renders but every CTA is
 *    "Ask your admin" (disabled). The backend re-checks company_admin +
 *    CSRF on every billing write — this is presentation only.
 *  - 503 billing_unavailable on checkout is the EXPECTED state in this
 *    build (Razorpay creds intentionally unset); the picker swaps the
 *    CTA to a graceful "Contact sales" link, NOT an error toast.
 *
 * Auth is cookie-only via the shared client; nothing here touches
 * localStorage / Authorization.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port
 * of frontend/src/app/(dashboard)/dashboard/settings/billing/page.tsx.
 */

import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CreditCard, Info } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/hooks/use-billing";
import { useSeats } from "@/hooks/use-seats";
import { isHRAdmin } from "@/lib/nav";
import { hasPoshAddon, trialEndNotice } from "@/lib/billing-advice";
import { poshOnlyPlanLine } from "@/lib/posh-bands";
import { POSH_ONLY_PLAN_CODE } from "@/types/billing";
import { toApiError } from "@/lib/api";
import { formatPaise } from "@/lib/expense-format";
import { fmtDate } from "@/lib/leave-format";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { StatusBadge } from "@/components/shared/status-badge";
import { PlanPicker } from "@/components/marketing/plan-picker";
import { AddonsSection } from "@/components/billing/addons-section";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/billing",
)({
  component: SettingsBillingRoute,
});

/**
 * The subscription read is company-admin only; anyone else got the raw
 * "Insufficient_role." (ICC QA, 28 Oct 2026). Say it plainly.
 */
function subscriptionErrorText(err: unknown): string {
  const e = toApiError(err);
  if (e.error === "forbidden" || /insufficient.?role/i.test(e.message)) {
    return "Only your company admin can see the subscription.";
  }
  return e.message || "Please refresh and try again.";
}

function SettingsBillingRoute() {
  const { user } = useAuth();
  const sub = useSubscription();

  const isCompanyAdmin = user?.role === "company_admin";
  // Live active headcount (the People seat meter's endpoint, HR-admin
  // only) — pre-fills the Pro calculator and drives the over-Free-cap
  // warning (persona round 2026-10).
  const seatsQ = useSeats(isHRAdmin(user?.role));
  const activeCount = seatsQ.data?.used ?? null;
  const freeLimit = seatsQ.data?.free_limit ?? null;
  const prefill = user
    ? {
        name: `${user.first_name} ${user.last_name}`.trim(),
        email: user.email,
      }
    : undefined;

  const subscription = sub.data?.subscription;
  const plan = subscription?.plan ?? null;
  // A trial / no-plan tenant: status is the source of truth, plan null.
  const isTrial = subscription?.status === "trial" || plan == null;
  // A paid plan that has ended (past its end date + 7 days, or cancelled):
  // the company is on Free terms until it renews, so the plan is not
  // "current" in the picker — it can be bought again.
  const paidEnded =
    plan != null && plan.price_paise > 0 && subscription?.in_force === false;
  const graceUntil = subscription?.grace_until ?? null;
  // POSH only (migration 162): a ₹0 base whose POSH is the add-on — its
  // status line comes from the POSH row (or the POSH trial), never "₹0".
  const onPoshOnly = plan?.code === POSH_ONLY_PLAN_CODE;
  const poshOnlyLine = onPoshOnly ? poshOnlyPlanLine(subscription) : null;

  // The expires_at date is a RENEWAL date while active, and an EXPIRY
  // when the subscription has lapsed/cancelled — label accordingly.
  const dateLabel =
    subscription?.status === "active" ? "Renews on" : "Access until";

  // A signup trial (no purchased plan) lands on Free when it ends — say
  // exactly what that means for THIS company.
  const trialNotice =
    subscription?.status === "trial" && plan == null
      ? trialEndNotice({
          trialEndsLabel: subscription.expires_at
            ? fmtDate(subscription.expires_at)
            : null,
          activeCount,
          freeLimit,
          poshAddon: hasPoshAddon(subscription.addons),
        })
      : null;

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Billing</h2>
            <p className="text-sm text-muted-foreground">
              Your subscription and plan.
            </p>
          </div>

          {/* Current subscription */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4 text-primary" />
                Current Subscription
              </CardTitle>
              <CardDescription>
                The plan and status for your organisation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sub.isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ) : sub.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>
                    Couldn&apos;t load your subscription
                  </AlertTitle>
                  <AlertDescription>
                    {subscriptionErrorText(sub.error)}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="text-lg font-semibold text-gray-900">
                        {plan ? plan.name : "Trial"}
                      </p>
                      {subscription && (
                        <StatusBadge status={subscription.status} />
                      )}
                    </div>

                    {plan && subscription?.covered_employees != null && (
                      <p
                        className="mt-1 text-sm text-muted-foreground"
                        data-testid="pack-coverage"
                      >
                        Covers up to {subscription.covered_employees} people (
                        {subscription.packs} pack
                        {subscription.packs === 1 ? "" : "s"}).
                      </p>
                    )}

                    {onPoshOnly ? (
                      <p
                        className="mt-1 text-sm text-muted-foreground"
                        data-testid="posh-only-status"
                      >
                        {poshOnlyLine}
                      </p>
                    ) : plan ? (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {formatPaise(plan.price_paise)}
                        {plan.billing_period === "monthly"
                          ? " / month"
                          : plan.billing_period === "yearly"
                            ? " / year"
                            : ` / ${plan.billing_period}`}
                        {" · "}
                        {(plan.unit_employees ?? 0) > 0
                          ? `billed per ${plan.unit_employees}-employee pack${
                              (plan.included_employees ?? 0) > 0
                                ? ` · first ${plan.included_employees} free`
                                : ""
                            }`
                          : plan.max_employees === 0
                            ? "Unlimited employees"
                            : `Up to ${plan.max_employees} employees`}
                      </p>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        You&apos;re on a trial.{" "}
                        {isCompanyAdmin
                          ? "Choose a plan below to activate your subscription."
                          : "Ask your company admin to choose a plan."}
                      </p>
                    )}

                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      {subscription?.started_at && (
                        <p>
                          Started:{" "}
                          <span className="font-medium text-gray-700">
                            {fmtDate(subscription.started_at)}
                          </span>
                        </p>
                      )}
                      {subscription?.expires_at && !onPoshOnly && (
                        <p>
                          {dateLabel}:{" "}
                          <span className="font-medium text-gray-700">
                            {fmtDate(subscription.expires_at)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {paidEnded && (
            <Alert
              data-testid="paid-plan-ended"
              className="border-amber-300 bg-amber-50 text-amber-900"
            >
              <AlertTriangle aria-hidden className="h-4 w-4" />
              <AlertTitle>
                {plan?.name} ended
                {subscription?.expires_at
                  ? ` on ${fmtDate(subscription.expires_at)}`
                  : ""}{" "}
                — renew to get it back
              </AlertTitle>
              <AlertDescription>
                You&apos;re on Free terms: {plan?.name} features are off and
                you can&apos;t add people beyond {freeLimit ?? 5}. Nobody is
                locked out and nothing is deleted.{" "}
                {isCompanyAdmin
                  ? "Choose a plan below to renew."
                  : "Ask your company admin to renew."}
              </AlertDescription>
            </Alert>
          )}

          {!paidEnded && graceUntil && (
            <Alert
              data-testid="paid-plan-grace"
              className="border-amber-300 bg-amber-50 text-amber-900"
            >
              <AlertTriangle aria-hidden className="h-4 w-4" />
              <AlertTitle>
                {plan?.name} ended
                {subscription?.expires_at
                  ? ` on ${fmtDate(subscription.expires_at)}`
                  : ""}
              </AlertTitle>
              <AlertDescription>
                It keeps working until {fmtDate(graceUntil)}. Renew before then
                to keep {plan?.name} features and your team size.
              </AlertDescription>
            </Alert>
          )}

          {trialNotice && (
            <Alert
              data-testid="trial-end-notice"
              className={
                trialNotice.warning
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : undefined
              }
            >
              {trialNotice.warning ? (
                <AlertTriangle aria-hidden className="h-4 w-4" />
              ) : (
                <Info aria-hidden className="h-4 w-4" />
              )}
              <AlertTitle>{trialNotice.title}</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 list-disc space-y-1 pl-4">
                  {trialNotice.lines.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Add-ons — layered modules (e.g. POSH) billed on top of the
              base plan. Self-fetches the available catalogue; held add-ons
              come from the subscription. */}
          {!sub.isLoading && !sub.isError && (
            <AddonsSection
              addons={subscription?.addons ?? []}
              isCompanyAdmin={isCompanyAdmin}
              prefill={prefill}
              activeCount={activeCount}
              poshOnly={onPoshOnly}
            />
          )}

          {/* Change / choose a plan */}
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-gray-900">
              {isTrial ? "Choose a Plan" : "Change Plan"}
            </h2>
            <p className="mb-6 mt-1 text-sm text-muted-foreground">
              {isCompanyAdmin
                ? "Pick the plan that fits your team. You'll be taken to secure checkout."
                : "Only your company admin can change the subscription. You can review the available plans below."}
            </p>

            {!sub.isLoading && (
              <PlanPicker
                isCompanyAdmin={isCompanyAdmin}
                currentPlanCode={paidEnded || graceUntil ? null : (plan?.code ?? null)}
                prefill={prefill}
                onSubscribed={() => sub.refetch()}
                activeCount={activeCount}
                freeLimit={freeLimit}
                currentPlanNote={poshOnlyLine}
              />
            )}

            <p className="mt-8 text-xs text-muted-foreground">
              All prices are exclusive of applicable GST (currently 18%).
              Payments are processed securely by Razorpay. See our{" "}
              <a
                href="/refund"
                className="font-medium text-primary hover:text-primary/80"
              >
                refund &amp; cancellation policy
              </a>
              .
            </p>
          </div>
        </div>
      </SettingsShell>
    </>
  );
}
